import { useState } from 'react';
import { QuestionAnswer, WhatsApp, Warehouse, LocalShipping, AccountBalanceWallet, Calculate, Download } from '@mui/icons-material'
import { Box, Button, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ROLES } from '../../constants';
import { startTourFromHelpCenter } from 'src/_store/entities/tutorial';
import { TOUR_IDS } from 'src/_modules/MapitTutorial/tours';

export default function HelpCenter() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { profile } = useSelector(state => state.entities.profile);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const isMerchant = profile?.merchant?.role === ROLES.merchant;

    const handleButtonClick = () => {
        if (isMerchant) {
            setDrawerOpen(true);
        }
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleWhatsAppClick = () => {
        const phone = profile?.merchant?.currentStatus === "WAITING" ? '966581849999' : '966597327471';
        window.open(`https://api.whatsapp.com/send/?phone=${phone}`, '_blank')
        setDrawerOpen(false);
    };

    const handleStartTour = (tourId) => {
        dispatch(startTourFromHelpCenter(tourId, handleDrawerClose));
    };

    const tutorialItems = [
        {
            icon: <Warehouse />,
            label: t("tutorialGuide.createWarehouse"),
            onClick: () => handleStartTour(TOUR_IDS.WAREHOUSE_TOUR)
        },
        {
            icon: <LocalShipping />,
            label: t("tutorialGuide.createShipment"),
            onClick: () => handleStartTour(TOUR_IDS.SHIPMENT_TOUR)
        },
        {
            icon: <AccountBalanceWallet />,
            label: t("tutorialGuide.chargeWallet"),
            onClick: () => handleStartTour(TOUR_IDS.CHARGE_WALLET_TOUR)
        },
        {
            icon: <Calculate />,
            label: t("tutorialGuide.calculateShipmentCost"),
            onClick: () => handleStartTour(TOUR_IDS.PRICE_CALCULATOR_TOUR)
        },
        {
            icon: <Download />,
            label: t("tutorialGuide.downloadAwb"),
            onClick: () => handleStartTour(TOUR_IDS.DOWNLOAD_AWB_TOUR)
        },
    ];

    // Drawer anchor: left for RTL, right for LTR
    //const drawerAnchor = theme.direction === 'rtl' ? 'left' : 'right';

    return (
        <>
            <Button
                startIcon={<QuestionAnswer />}
                variant="contained"
                color="secondary"
                {...(!isMerchant && {
                    href: "https://api.whatsapp.com/send/?phone=966581849999",
                    target: "_blank"
                })}
                onClick={isMerchant ? handleButtonClick : undefined}
                sx={theme => ({
                    position: 'fixed',
                    top: "75%",
                    right: theme.direction === "rtl" ? -32 : -28,
                    zIndex: 1200,
                    rotate: '-90deg',
                    textTransform: 'none',
                })}>
                {t("tutorialGuide.help")}
            </Button>

            {isMerchant && (
                <Drawer
                    anchor={"right"}
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                >
                    <Box
                        sx={{
                            width: 300,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'background.paper'
                        }}
                        pt={8}
                        role="presentation"
                    >
                        {/* Header Section */}
                        {/* <Box
                            sx={{
                                p: 3,
                                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'primary.contrastText',
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {t(tutorialGuide.gettingStartedGuide)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t(tutorialGuide.gettingStartedDescription)}
                            </Typography>
                        </Box> */}

                        {/* Tutorial Items */}
                        <Box sx={{ p: 2, flexGrow: 1 }}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ px: 2, display: 'block', mb: 1 }}
                            >
                                {t("tutorialGuide.quickTutorials")}
                            </Typography>
                            <List disablePadding>
                                {tutorialItems.map((item, index) => (
                                    <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            onClick={item.onClick}
                                            sx={{
                                                borderRadius: 2,
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'secondary.main',
                                                    }
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 44, color: 'text.secondary' }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.label}
                                                primaryTypographyProps={{
                                                    fontWeight: 500,
                                                    fontSize: '0.95rem'
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        {/* Customer Support Section */}
                        <Box sx={{ p: 2, mt: 'auto' }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ px: 2, display: 'block', mb: 1 }}
                            >
                                {t("tutorialGuide.needMoreHelp")}
                            </Typography>
                            <List disablePadding>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={handleWhatsAppClick}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: 'rgba(37, 211, 102, 0.08)',
                                            '&:hover': {
                                                bgcolor: 'rgba(37, 211, 102, 0.16)',
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 44 }}>
                                            <WhatsApp sx={{ color: '#25D366' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t("tutorialGuide.customerSupport")}
                                            secondary={t("tutorialGuide.chatOnWhatsApp")}
                                            primaryTypographyProps={{
                                                fontWeight: 600,
                                                color: '#25D366'
                                            }}
                                            secondaryTypographyProps={{
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Box>
                    </Box>
                </Drawer>
            )}
        </>
    )
}
